import { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "@legal-500/legal500-design-system";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal.js";
import { usePermissions } from "../../../hooks/usePermissions.js";
import {
  APPLY_CHUNK_SIZE,
  websiteRelabelService,
  type ApplyRowResult,
  type BulkWebsiteRelabelApplyResponse,
  type ValidateRowResult,
} from "../../../api/profiles/websiteRelabelService.js";
import {
  fileSizeProblem,
  parseManifest,
  problemDetail,
  toApiRow,
  REQUIRED_COLUMNS,
  type ManifestRow,
  type ParseManifestResult,
} from "./parseManifest.js";
import { downloadPlannedRollbackManifest, downloadRollbackManifest } from "./rollbackManifest.js";
import {
  isAnomaly,
  isSelectable,
  VerdictTable,
  type RelabelEntry,
  type VerdictFilter,
} from "./VerdictTable.js";
import styles from "./WebsiteRelabel.module.css";

/**
 * Fixes mislabelled website/language_code values on firm and lawyer profiles
 * from a locally-generated manifest CSV (SOFTSUPP-532, a WordPress backfill
 * artefact of ~3,750 rows).
 *
 * The flow is deliberately two-phase: validate is a server-side dry run whose
 * per-row verdicts are previewed here, and apply only ever sends the rows that
 * validated as ok AND are still selected. Rows the client can already tell are
 * invalid never reach the API at all — a single invalid website would 400 the
 * whole batch. Every applied row's pre-update state comes back in the response,
 * so the run ends with a downloadable rollback manifest rather than a leap of
 * faith.
 *
 * A weaker rollback is also offered BEFORE applying, built from the selected
 * rows' own current values, because a tab that dies mid-run would otherwise
 * leave the reviewer with no undo at all. Both files exist on purpose and the
 * post-apply one stays authoritative — see rollbackManifest.ts.
 *
 * The run itself is chunked (APPLY_CHUNK_SIZE) for the same reason the rollback
 * exists: the response is the only copy of the pre-update state, so the batch
 * is broken into requests small enough that a gateway timeout cannot swallow
 * the lot. A chunk that fails stops the run, and everything the earlier chunks
 * returned is kept and still downloadable.
 *
 * Every ok row starts selected. Skipping a row leaves that content invisible on
 * the target site, so the safe default is to apply — deselection is a reviewer
 * overruling the manifest for a specific row, usually one the generator flagged
 * as an anomaly and whose content preview says otherwise.
 */

type Phase = "idle" | "validating" | "previewed" | "applying" | "done";

/** The applied run, kept for the rollback download. Results and rows match by index. */
type AppliedRun = {
  results: ApplyRowResult[];
  rows: ManifestRow[];
};

/** How far a chunked run has got, for the progress display. */
type ApplyProgress = {
  /** Rows whose chunk has come back. */
  done: number;
  total: number;
};

export default function WebsiteRelabel() {
  const { hasPermission } = usePermissions();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [entries, setEntries] = useState<RelabelEntry[]>([]);
  const [filter, setFilter] = useState<VerdictFilter>("all");
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [applied, setApplied] = useState<AppliedRun | null>(null);
  const [applyProgress, setApplyProgress] = useState<ApplyProgress>({ done: 0, total: 0 });
  /**
   * Whether a planned rollback has been taken for the file currently loaded.
   * Only drives the confirmation dialog's nudge — it never blocks applying.
   */
  const [hasPlannedRollback, setHasPlannedRollback] = useState(false);
  /** Line numbers of the rows this run will write. Unique per manifest row. */
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set());

  // This is a developer tool: a one-off bulk migration with a far larger blast
  // radius than editing a single profile, so it is gated on developer.write —
  // the same permission as its Developer-section neighbours (e.g. the CRM
  // webhook tester) — rather than on profiles.*, whose holders cannot even see
  // the Developer nav group.
  const canRead = hasPermission("developer.write");
  const canWrite = canRead;

  const reset = useCallback(() => {
    setFileName(null);
    setEntries([]);
    setFilter("all");
    setError(null);
    setIsConfirming(false);
    setApplied(null);
    setApplyProgress({ done: 0, total: 0 });
    setHasPlannedRollback(false);
    setSelected(new Set());
    setPhase("idle");
  }, []);

  const toggleRow = useCallback((lineNumber: number) => {
    setSelected(current => {
      const next = new Set(current);
      if (!next.delete(lineNumber)) {
        next.add(lineNumber);
      }
      return next;
    });
  }, []);

  const setRows = useCallback((lineNumbers: number[], isSelected: boolean) => {
    setSelected(current => {
      const next = new Set(current);
      for (const lineNumber of lineNumbers) {
        if (isSelected) {
          next.add(lineNumber);
        } else {
          next.delete(lineNumber);
        }
      }
      return next;
    });
  }, []);

  const loadFile = useCallback(
    async (file: File) => {
      reset();
      setFileName(file.name);

      // Before `file.text()`, not after: reading a huge file into a string is
      // what freezes the tab, so the row limit inside parseManifest can only
      // ever speak once it is too late.
      const tooBig = fileSizeProblem(file.size);
      if (tooBig) {
        setError(tooBig);
        return;
      }

      let parsed: ParseManifestResult;
      try {
        parsed = parseManifest(await file.text());
      } catch (parseError) {
        setError(parseError instanceof Error ? parseError.message : String(parseError));
        return;
      }
      if (parsed.rows.length === 0) {
        setError("That file has a header but no rows.");
        return;
      }

      const cleanRows = parsed.rows.filter(row => row.problem === null);

      // Verdicts come back in request order, so they are merged back by index —
      // a manifest may contain duplicate ids, and only position is unambiguous.
      // Invalid rows were never sent, so the cursor advances past clean rows only.
      const mergeVerdicts = (results: ValidateRowResult[]): RelabelEntry[] => {
        let cursor = 0;
        return parsed.rows.map(row => {
          if (row.problem !== null) {
            return { row, verdict: "invalid" as const, detail: problemDetail(row) };
          }
          const result = results[cursor];
          cursor += 1;
          const entry: RelabelEntry = {
            row,
            verdict: result?.status ?? "failed",
            detail: result?.detail ?? null,
          };
          // Assigned conditionally rather than as `preview: result?.preview`,
          // so an API that sends no preview leaves the key absent entirely.
          if (result?.preview) {
            entry.preview = result.preview;
          }
          return entry;
        });
      };

      /** Every applicable row starts selected — see the note at the top of the file. */
      const showPreview = (merged: RelabelEntry[]) => {
        setEntries(merged);
        setSelected(new Set(merged.filter(isSelectable).map(entry => entry.row.lineNumber)));
        setPhase("previewed");
      };

      // A file of nothing but invalid rows still gets a preview — the API
      // requires at least one row, so there is nothing to validate remotely.
      if (cleanRows.length === 0) {
        showPreview(mergeVerdicts([]));
        return;
      }

      setPhase("validating");
      try {
        const response = await websiteRelabelService.validate(cleanRows.map(toApiRow));
        showPreview(mergeVerdicts(response.results));
      } catch (validateError) {
        setError(validateError instanceof Error ? validateError.message : String(validateError));
        setPhase("idle");
      }
    },
    [reset],
  );

  /** The rows this run will write: validate said ok and the reviewer left them ticked. */
  const selectedEntries = useMemo(
    () => entries.filter(entry => isSelectable(entry) && selected.has(entry.row.lineNumber)),
    [entries, selected],
  );
  const selectedCount = selectedEntries.length;

  /**
   * Hands the reviewer an undo file BEFORE the run, so a tab that crashes or is
   * closed mid-apply does not leave them with nothing.
   *
   * Deliberately not invalidated when the selection changes afterwards.
   * Uploading a rollback manifest re-validates its `current_*` columns against
   * live database state, so a row that ultimately was not changed — deselected,
   * failed, skipped — comes back as `stale_current` and is dropped. A planned
   * rollback can therefore only ever cover less than it lists, never more; it
   * cannot revert something that never happened.
   */
  const downloadPlannedRollback = useCallback(() => {
    downloadPlannedRollbackManifest(selectedEntries, new Date());
    setHasPlannedRollback(true);
  }, [selectedEntries]);

  /**
   * Runs the selection through the apply endpoint in chunks of APPLY_CHUNK_SIZE,
   * sequentially, banking every chunk that comes back.
   *
   * Deliberately resolves rather than throws, whatever happens. The confirmation
   * dialog is dismissed the moment the run starts — a run this long needs
   * progress the operator can watch and cannot dismiss by accident — so a
   * rethrow would land on an unmounted dialog and the failure would vanish.
   * Every outcome is written to page state instead, which stays mounted.
   */
  const applySelectedRows = useCallback(async () => {
    // Only the rows validate blessed and the reviewer kept. Everything else —
    // invalid, stale, noop — stays exactly as the preview showed it.
    const rowsToApply = selectedEntries.map(entry => entry.row);

    /**
     * Results and the rows they describe, accumulated across chunks and kept
     * index-aligned: the rollback manifest joins the two by position, and a row
     * is only ever pushed alongside the result that came back for it. The
     * matching itself happens strictly WITHIN a chunk, against that chunk's own
     * response, so no offset can drift from one request to the next.
     */
    const results: ApplyRowResult[] = [];
    const resultRows: ManifestRow[] = [];

    /**
     * Ends the run: folds the outcomes gathered so far into the table, keeps
     * them for the rollback download, and shows the result view. Called for a
     * clean run and for one cut short alike — a partial run's rollback data is
     * the most valuable thing on the page and must never be dropped.
     */
    const settle = (failure: string | null) => {
      // Keyed by row object identity, which survives duplicate ids in the file.
      const outcomeByRow = new Map<ManifestRow, ApplyRowResult>();
      resultRows.forEach((row, index) => {
        const result = results[index];
        if (result) {
          outcomeByRow.set(row, result);
        }
      });
      const wasSelected = new Set<ManifestRow>(rowsToApply);

      setEntries(current =>
        current.map(entry => {
          const outcome = outcomeByRow.get(entry.row);
          if (outcome) {
            return { ...entry, verdict: outcome.status, detail: outcome.detail ?? null };
          }
          // An ok row with no outcome was never written, and has to stop reading
          // as "ok" or the result view would report it as applied. The two ways
          // that happens are different stories for the reviewer: one is their own
          // decision, the other is work the run never got to.
          if (entry.verdict === "ok") {
            return wasSelected.has(entry.row)
              ? {
                  ...entry,
                  verdict: "unsent" as const,
                  detail: "the run stopped before this row was applied",
                }
              : { ...entry, verdict: "skipped" as const, detail: "deselected before applying" };
          }
          return entry;
        }),
      );
      setApplied({ results, rows: resultRows });
      setError(failure);
      setPhase("done");
    };

    setError(null);
    setApplyProgress({ done: 0, total: rowsToApply.length });
    setIsConfirming(false);
    setPhase("applying");

    /**
     * A request that failed may still have written rows: a gateway timeout cuts
     * the connection, not the transaction the API is part-way through. So no
     * message here may claim a row was untouched — only re-validating against
     * live state can establish that, which is exactly what the advice says.
     */
    const retryAdvice =
      "Rows in the failed batch may or may not have been written — " +
      "re-upload the manifest to re-validate before retrying.";

    let completedChunks = 0;

    for (let start = 0; start < rowsToApply.length; start += APPLY_CHUNK_SIZE) {
      const chunk = rowsToApply.slice(start, start + APPLY_CHUNK_SIZE);

      let response: BulkWebsiteRelabelApplyResponse;
      try {
        response = await websiteRelabelService.apply(chunk.map(toApiRow));
      } catch (applyError) {
        const message = applyError instanceof Error ? applyError.message : String(applyError);
        if (completedChunks === 0) {
          // Nothing came back at all, so there is no rollback data to protect
          // and no result worth showing: back to the preview with the selection
          // intact, which is the state a retry starts from.
          setPhase("previewed");
          setError(
            `The first batch of ${chunk.length.toLocaleString()} rows failed: ${message} ${retryAdvice}`,
          );
          return;
        }
        const written = results.filter(result => result.status === "ok" && result.previous).length;
        settle(
          `Stopped after ${results.length.toLocaleString()} of ` +
            `${rowsToApply.length.toLocaleString()} rows: ${message} ` +
            `${written.toLocaleString()} row${written === 1 ? " was" : "s were"} changed and can ` +
            `be rolled back — download the manifest below first. ${retryAdvice}`,
        );
        return;
      }
      completedChunks += 1;

      // Index-matched against THIS chunk: response.results[i] describes chunk[i].
      chunk.forEach((row, index) => {
        const result = response.results[index];
        if (result) {
          results.push(result);
          resultRows.push(row);
        }
      });
      setApplyProgress({ done: start + chunk.length, total: rowsToApply.length });
    }

    settle(null);
  }, [selectedEntries]);

  const verdictCounts = useMemo(() => {
    const tally = new Map<string, number>();
    for (const entry of entries) {
      tally.set(entry.verdict, (tally.get(entry.verdict) ?? 0) + 1);
    }
    return (verdict: string) => tally.get(verdict) ?? 0;
  }, [entries]);

  // Memoised because the table rebuilds a row object per entry whenever this
  // identity changes, and a manifest runs to thousands of rows.
  const selectionControls = useMemo(
    () => ({ selected, onToggleRow: toggleRow, onSetRows: setRows }),
    [selected, toggleRow, setRows],
  );

  const okCount = verdictCounts("ok");
  const invalidCount = verdictCounts("invalid");
  const anomalyCount = useMemo(() => entries.filter(isAnomaly).length, [entries]);
  const failedCount = verdictCounts("failed");
  /** Selected rows a stopped run never reached — outstanding, not skipped. */
  const unsentCount = verdictCounts("unsent");

  /** Rows the apply call actually wrote — the only ones a rollback can target. */
  const appliedCount = useMemo(
    () =>
      applied
        ? applied.results.filter(result => result.status === "ok" && result.previous).length
        : 0,
    [applied],
  );

  if (!canRead && !canWrite) {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied} role="alert">
          <h2>Access Denied</h2>
          <p>You do not have permission to view the Profile Website Change tool.</p>
        </div>
      </div>
    );
  }

  const isBusy = phase === "validating" || phase === "applying";

  return (
    <div className={styles.page}>
      <PageHeader
        title="Profile Website Change"
        subtitle="Change the website and language of firm and lawyer profiles from a manifest CSV"
      />

      {error ? (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.card}>
        <div className={styles.cardTitle}>1 · Choose manifest</div>
        <div
          className={`${styles.drop} ${isDragging ? styles.dropActive : ""}`}
          onDragOver={event => {
            // Still prevented while busy, or the browser navigates away to the
            // dropped file. Only the drop-active styling is suppressed.
            event.preventDefault();
            if (!isBusy) {
              setIsDragging(true);
            }
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={event => {
            event.preventDefault();
            setIsDragging(false);
            // Loading a second file mid-call would reset() out from under the
            // request that is still in flight: its verdicts would land afterwards,
            // over the new file's state.
            if (isBusy) {
              return;
            }
            const dropped = event.dataTransfer.files[0];
            if (dropped) {
              void loadFile(dropped);
            }
          }}
        >
          <div className={styles.dropTitle}>
            {fileName ?? "Drop a manifest CSV here, or browse"}
          </div>
          <div className={styles.dropHint}>
            Columns are matched by name. Required: <code>{REQUIRED_COLUMNS.join(",")}</code>.
            Optional:{" "}
            <code>source_site,source_post_id,detected_lang,anomaly,status_hint,name,excerpt</code>.
          </div>
          <input
            ref={fileInputRef}
            type="file"
            aria-label="Profile website change manifest CSV"
            accept=".csv,.tsv,.txt,text/csv"
            className={styles.hiddenInput}
            // Visually hidden, not display:none, so it stays reachable by keyboard
            // and assistive tech — which means it needs the same disabled state as Browse.
            disabled={isBusy}
            onChange={event => {
              const chosen = event.target.files?.[0];
              // Clear the input's value, otherwise picking the SAME file again fires
              // no change event and appears to do nothing — which is exactly what
              // someone re-validating a corrected manifest would try.
              event.target.value = "";
              if (chosen && !isBusy) {
                void loadFile(chosen);
              }
            }}
          />
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBusy}
          >
            Browse
          </Button>
        </div>
      </div>

      {phase === "validating" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Validating…</div>
        </div>
      ) : null}

      {/*
       * A run of thousands of rows takes minutes, and the confirmation dialog is
       * gone by now on purpose, so this card is the whole of what the operator
       * has to look at. Without it the page reads as dead and the natural next
       * move is a reload — which abandons the run and loses the rollback data.
       */}
      {phase === "applying" ? (
        <div className={styles.card}>
          <div className={styles.cardTitle}>Applying…</div>
          <div className={styles.progress} role="status" aria-live="polite">
            {`${applyProgress.done.toLocaleString()} / ${applyProgress.total.toLocaleString()} rows`}
          </div>
          <div className={styles.rowNote}>
            {applyProgress.total > APPLY_CHUNK_SIZE
              ? `Sent in batches of ${APPLY_CHUNK_SIZE.toLocaleString()}. Keep this tab open — the rollback manifest is built from the responses.`
              : "Keep this tab open — the rollback manifest is built from the responses."}
          </div>
        </div>
      ) : null}

      {invalidCount > 0 && (phase === "previewed" || phase === "done") ? (
        <div className={`${styles.banner} ${styles.bannerWarning}`}>
          {invalidCount} row{invalidCount === 1 ? "" : "s"} invalid — not sent for validation.
        </div>
      ) : null}

      {anomalyCount > 0 && phase === "previewed" ? (
        <div className={`${styles.banner} ${styles.bannerWarning}`}>
          {anomalyCount} row{anomalyCount === 1 ? "" : "s"} flagged by the manifest generator.
        </div>
      ) : null}

      {phase === "previewed" ? (
        <>
          <div className={styles.card}>
            <div className={styles.cardTitle}>Validation preview</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{okCount}</div>
                <div className={styles.tileLabel}>Ok</div>
              </div>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{selectedCount}</div>
                <div className={styles.tileLabel}>Selected — will be applied</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>{verdictCounts("noop")}</div>
                <div className={styles.tileLabel}>No-op</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{verdictCounts("stale_current")}</div>
                <div className={styles.tileLabel}>Stale current</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{verdictCounts("not_found")}</div>
                <div className={styles.tileLabel}>Not found</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{verdictCounts("constraint_violation")}</div>
                <div className={styles.tileLabel}>Constraint violation</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{verdictCounts("group_collision")}</div>
                <div className={styles.tileLabel}>Group collision</div>
              </div>
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{invalidCount}</div>
                <div className={styles.tileLabel}>Invalid rows</div>
              </div>
            </div>
          </div>

          <VerdictTable
            entries={entries}
            filter={filter}
            onFilterChange={setFilter}
            mode="validate"
            selection={selectionControls}
          />

          <div className={styles.toolbar}>
            <Button
              variant="outlined"
              disabled={selectedCount === 0}
              onClick={downloadPlannedRollback}
            >
              Download rollback manifest
            </Button>
            <span className={styles.rowNote}>
              Saves as <code>…-rollback-planned.csv</code>.
            </span>
            <span className={styles.spacer} />
            <Button variant="outlined" onClick={reset}>
              Cancel
            </Button>
            {canWrite ? (
              <Button
                variant="contained"
                onClick={() => setIsConfirming(true)}
                disabled={selectedCount === 0 || isBusy}
              >
                Apply {selectedCount} change{selectedCount === 1 ? "" : "s"}
              </Button>
            ) : (
              <span className={styles.rowNote}>
                Applying changes requires the developer.write permission.
              </span>
            )}
          </div>
        </>
      ) : null}

      {phase === "done" ? (
        <>
          {failedCount > 0 ? (
            <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
              {failedCount} row{failedCount === 1 ? "" : "s"} failed to apply. Download the rollback
              manifest before retrying.
            </div>
          ) : null}

          <div className={styles.card}>
            <div className={styles.cardTitle}>Result</div>
            <div className={styles.tiles}>
              <div className={`${styles.tile} ${styles.tileOk}`}>
                <div className={styles.tileNumber}>{okCount}</div>
                <div className={styles.tileLabel}>Applied</div>
              </div>
              <div className={`${styles.tile} ${styles.tileNeutral}`}>
                <div className={styles.tileNumber}>
                  {entries.length - okCount - failedCount - unsentCount}
                </div>
                <div className={styles.tileLabel}>Skipped</div>
              </div>
              {/* Only when a run was cut short — otherwise it is a tile of zero
                  on every clean run, which reads as a problem where none exists. */}
              {unsentCount > 0 ? (
                <div className={`${styles.tile} ${styles.tileError}`}>
                  <div className={styles.tileNumber}>{unsentCount}</div>
                  <div className={styles.tileLabel}>Not sent</div>
                </div>
              ) : null}
              <div className={`${styles.tile} ${styles.tileError}`}>
                <div className={styles.tileNumber}>{failedCount}</div>
                <div className={styles.tileLabel}>Failed</div>
              </div>
            </div>
          </div>

          <VerdictTable entries={entries} filter={filter} onFilterChange={setFilter} mode="apply" />

          <div className={styles.toolbar}>
            <Button variant="outlined" onClick={reset}>
              Upload another file
            </Button>
            <span className={styles.spacer} />
            <Button
              variant="contained"
              disabled={appliedCount === 0}
              onClick={() => {
                if (applied) {
                  downloadRollbackManifest(applied.results, applied.rows, new Date());
                }
              }}
            >
              Download rollback manifest
            </Button>
          </div>
        </>
      ) : null}

      {isConfirming ? (
        <ConfirmationModal
          title="Apply profile website change"
          message={`${selectedCount} profile row${selectedCount === 1 ? "" : "s"} will be changed.`}
          warningMessage={
            hasPlannedRollback
              ? "A rollback manifest is offered after the run. It is the only undo."
              : "No rollback manifest downloaded. A rollback manifest is the only undo."
          }
          confirmButtonText={`Apply ${selectedCount} change${selectedCount === 1 ? "" : "s"}`}
          // A stray click beside the dialog must not stand in for a decision
          // about thousands of profile rows — Cancel and Escape still do.
          backdropClick={false}
          onClose={() => setIsConfirming(false)}
          onConfirm={applySelectedRows}
        >
          {/* A nudge, never a gate: an operator who knows what they are doing
              can still confirm without taking the planned file. */}
          {hasPlannedRollback ? null : (
            <Button variant="outlined" onClick={downloadPlannedRollback}>
              Download rollback manifest first
            </Button>
          )}
        </ConfirmationModal>
      ) : null}
    </div>
  );
}
